/** @jsxImportSource @emotion/react */
import { Box, Button, Heading, Input, Text } from '@landtrustinc/design-system';
import React from 'react';

import type { OtpContext } from '../../../types';
import { useCodeStep } from '../hooks/useCodeStep';
import type { AnalyticsHandler } from '../hooks/useSignupFlow';
import { helperTextStyles, resendLinkStyles } from './styles';

type CodeStepProps = {
  apiBaseUrl: string;
  email: string;
  context?: OtpContext;
  onVerified: (idpToken: string, tokenExpiresAt: number, isExistingUser: boolean) => void;
  onAnalyticsEvent?: AnalyticsHandler;
};

export const CodeStep: React.FC<CodeStepProps> = ({
  apiBaseUrl,
  email,
  context,
  onVerified,
  onAnalyticsEvent,
}) => {
  const {
    code,
    setCode,
    codeError,
    verifyingCode,
    sendingCode,
    resendCooldown,
    handleVerifyCode,
    handleResendCode,
  } = useCodeStep({
    apiBaseUrl,
    email,
    context,
    onVerified,
    onAnalyticsEvent,
  });

  return (
    <Box display="flex" flexDirection="column" gap="var(--spacing-3)">
      <Heading size="xs" fontWeight="extrabold" textAlign="center" textWrap="wrap">
        Check your email
      </Heading>

      <Box mb="var(--spacing-3)">
        <Text size="md" textAlign="center">
          We&apos;ve sent a login code to {email}. Enter your code to create your account and save
          this property.
        </Text>
      </Box>

      <Box display="flex" flexDirection="column" gap="var(--spacing-1)" mb="var(--spacing-3)">
        <Text size="md" fontWeight="bold">
          Enter Verification Code
        </Text>
        <Input
          placeholder="Enter code"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          maxLength={9}
          error={codeError}
          autoComplete="one-time-code"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && code.length === 8 && !verifyingCode) {
              handleVerifyCode();
            }
          }}
        />
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Text size="xs" color="var(--text-secondary)" css={helperTextStyles}>
            We sent a code to your inbox
          </Text>
          {resendCooldown > 0 ? (
            <Text size="xs" color="var(--text-secondary)" css={helperTextStyles}>
              Resend in {resendCooldown}s
            </Text>
          ) : (
            <Text size="xs" css={resendLinkStyles} onClick={handleResendCode}>
              {sendingCode ? 'Sending...' : 'Resend code'}
            </Text>
          )}
        </Box>
      </Box>

      {codeError && (
        <Text size="sm" color="var(--text-error)" textAlign="center">
          {codeError}
        </Text>
      )}

      <Button
        variant="primary"
        fullWidth
        onClick={handleVerifyCode}
        disabled={!code.length || verifyingCode}
        isLoading={verifyingCode}
      >
        Continue
      </Button>
    </Box>
  );
};
